const INTENT_PREFIX = 'treechat-ordinal-send-intent:'
const INTENT_TTL_MS = 15 * 60 * 1000
const runtimeIntents = new Map<string, OrdinalSendIntent>()

export type OrdinalSendIntent = {
    userId: string
    deviceId: string
    ordinalId: string
    recipientAddress: string
    ordinalOutpoint: string
    createdAt: number
}

function normalizeOutpoint(outpoint: string): string {
    const normalized = outpoint.replace(/_([0-9]+)$/, ':$1').toLowerCase()
    if (!/^[0-9a-f]{64}:[0-9]+$/.test(normalized)) {
        throw new Error('Invalid ordinal send outpoint')
    }
    return normalized
}

function intentKey(ordinalId: string, recipientAddress: string, ordinalOutpoint: string): string {
    return `${INTENT_PREFIX}${encodeURIComponent(ordinalId)}:${encodeURIComponent(
        recipientAddress,
    )}:${normalizeOutpoint(ordinalOutpoint)}`
}

export function saveOrdinalSendIntent(intent: OrdinalSendIntent): void {
    const key = intentKey(intent.ordinalId, intent.recipientAddress, intent.ordinalOutpoint)
    const normalized = { ...intent, ordinalOutpoint: normalizeOutpoint(intent.ordinalOutpoint) }
    runtimeIntents.set(key, normalized)
    try {
        localStorage.setItem(key, JSON.stringify(normalized))
    } catch {
        // The active page can still sign when mobile storage is restricted.
    }
}

export function getOrdinalSendIntent(
    ordinalId: string,
    recipientAddress: string,
    ordinalOutpoint: string,
): OrdinalSendIntent | null {
    const key = intentKey(ordinalId, recipientAddress, ordinalOutpoint)
    const runtime = runtimeIntents.get(key)
    if (runtime && Date.now() - runtime.createdAt <= INTENT_TTL_MS) return runtime
    let serialized: string | null = null
    try {
        serialized = localStorage.getItem(key)
    } catch {
        return null
    }
    if (!serialized) return null
    try {
        const intent = JSON.parse(serialized) as OrdinalSendIntent
        if (
            intent.ordinalId !== ordinalId ||
            intent.recipientAddress !== recipientAddress ||
            normalizeOutpoint(intent.ordinalOutpoint) !== normalizeOutpoint(ordinalOutpoint) ||
            !Number.isSafeInteger(intent.createdAt) ||
            intent.createdAt > Date.now() ||
            Date.now() - intent.createdAt > INTENT_TTL_MS
        ) {
            clearOrdinalSendIntent(ordinalId, recipientAddress, ordinalOutpoint)
            return null
        }
        return intent
    } catch {
        clearOrdinalSendIntent(ordinalId, recipientAddress, ordinalOutpoint)
        return null
    }
}

export function clearOrdinalSendIntent(
    ordinalId: string,
    recipientAddress: string,
    ordinalOutpoint: string,
): void {
    const key = intentKey(ordinalId, recipientAddress, ordinalOutpoint)
    runtimeIntents.delete(key)
    try {
        localStorage.removeItem(key)
    } catch {}
}
